import type { ComponentType } from 'react';

import { cn } from '../../../shared/lib/cn';
import {
  CheckIcon,
  CloseIcon,
  MergeIcon,
  PauseIcon,
  PlayIcon,
  RecoveredIcon,
  RedeliverIcon,
  StopIcon,
  WaitIcon,
  type TIconProps,
} from '../../../shared/ui/icons';
import { StatusDisc } from '../../../shared/ui/status-disc';
import type { IStatusView, TStatusShape, TStatusTone } from '../lib';

const GLYPH: Record<TStatusShape, ComponentType<TIconProps>> = {
  done: CheckIcon,
  recovered: RecoveredIcon,
  failed: CloseIcon,
  retrying: RedeliverIcon,
  waiting: WaitIcon,
  paused: PauseIcon,
  stopped: StopIcon,
  merged: MergeIcon,
  running: PlayIcon,
};

const WORD: Record<TStatusTone, string> = {
  success: 'text-primary',
  failure: 'text-danger',
  motion: 'text-primary',
  suppressed: 'text-muted',
};

export const StatusGlyph = ({ view }: { view: IStatusView }) => (
  <StatusDisc tone={view.tone} icon={GLYPH[view.shape]} />
);

interface IRunStatusProps {
  view: IStatusView;
  /** A panel has the room to wrap; a table cell cuts the note first, the word last. */
  wrap?: boolean;
  className?: string;
}

// The note takes only the room the label leaves (`flex: 1`, a zero basis), so "retry in under a
// minute" is what gets cut in a narrow cell, never "Retrying · 4 of 5".
export const RunStatus = ({ view, wrap, className }: IRunStatusProps) => (
  <span
    className={cn(
      'flex min-w-0 gap-2 text-data',
      wrap ? 'flex-wrap items-center' : 'items-center',
      className,
    )}
  >
    <StatusGlyph view={view} />
    <span
      className={cn(wrap ? 'min-w-0' : 'min-w-0 truncate', WORD[view.tone])}
      title={wrap ? undefined : view.label}
    >
      {view.label}
    </span>
    {view.note && (
      <span
        className={cn('text-meta text-faint', wrap ? '' : 'min-w-0 flex-1 truncate')}
        title={view.note}
      >
        {view.note}
      </span>
    )}
  </span>
);
